<!-- 全年账实差异分析看板 -->
<template>
  <div class="annual-diff-page">
    <section class="filter-panel">
      <el-form :model="queryForm" size="small" label-width="72px" class="filter-form">
        <el-form-item label="统计年份">
          <el-date-picker
            v-model="queryForm.year"
            type="year"
            value-format="yyyy"
            placeholder="选择年份"
            :clearable="false"
            @change="handleYearChange"
          />
        </el-form-item>
        <el-form-item label="客户">
          <el-select
            v-model="queryForm.custNo"
            filterable
            remote
            clearable
            reserve-keyword
            :remote-method="loadCustomerOptions"
            :loading="customerLoading"
            placeholder="全部客户"
          >
            <el-option
              v-for="item in customerOptions"
              :key="item.id"
              :label="item.deptName || item.id"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="煤种">
          <el-select
            v-model="queryForm.cargoNo"
            filterable
            remote
            clearable
            reserve-keyword
            :remote-method="loadCargoOptions"
            :loading="cargoLoading"
            placeholder="全部煤种"
          >
            <el-option
              v-for="item in cargoOptions"
              :key="item.varNo"
              :label="item.varName || item.varNo"
              :value="item.varNo"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="库区">
          <el-select
            v-model="queryForm.stockCode"
            filterable
            remote
            clearable
            reserve-keyword
            :remote-method="loadStoreOptions"
            :loading="storeLoading"
            placeholder="全部库区"
          >
            <el-option
              v-for="item in storeOptions"
              :key="item.storeNo"
              :label="item.storeName || item.storeNo"
              :value="item.storeNo"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="库位">
          <el-select
            v-model="queryForm.locationCode"
            filterable
            remote
            clearable
            reserve-keyword
            :remote-method="loadLocationOptions"
            :loading="locationLoading"
            placeholder="全部库位"
          >
            <el-option
              v-for="item in locationOptions"
              :key="item.locationCode"
              :label="item.locationName || item.locationCode"
              :value="item.locationCode"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="差异类型">
          <el-select v-model="queryForm.diffType" clearable placeholder="全部类型">
            <el-option v-for="item in diffTypeOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item class="filter-actions">
          <el-button type="primary" icon="el-icon-search" :loading="pageLoading" @click="handleSearch">查询</el-button>
          <el-button icon="el-icon-refresh-left" @click="handleReset">重置</el-button>
          <el-tooltip v-if="!canExport" content="当前账号暂无导出权限" placement="top">
            <span>
              <el-button type="warning" plain icon="el-icon-download" disabled>导出</el-button>
            </span>
          </el-tooltip>
          <el-button
            v-else
            type="warning"
            plain
            icon="el-icon-download"
            :loading="exportLoading"
            @click="handleExport"
          >导出</el-button>
        </el-form-item>
      </el-form>
    </section>

    <el-alert
      v-if="pageError"
      class="state-alert"
      :title="pageError"
      type="error"
      show-icon
      :closable="false"
    />

    <section v-loading="summaryLoading" class="summary-grid">
      <div v-for="card in summaryCards" :key="card.key" :class="['metric-card', card.accent]">
        <div class="metric-head">
          <span>{{ card.label }}</span>
        </div>
        <div class="metric-value">
          {{ card.value }}<small>{{ card.unit }}</small>
        </div>
        <div class="metric-foot">{{ card.sub }}</div>
      </div>
    </section>

    <section class="analysis-grid">
      <div v-loading="summaryLoading" class="panel">
        <div class="panel-head">
          <span>差异类型分布</span>
          <span>按差异明细数</span>
        </div>
        <div v-show="hasTypeDistribution" ref="typeChart" class="chart-box" />
        <el-empty v-show="!hasTypeDistribution && !summaryLoading" :image-size="80" description="暂无差异类型数据" />
      </div>
      <div v-loading="summaryLoading" class="panel">
        <div class="panel-head">
          <span>月度账实差异趋势</span>
          <span>吨 / %</span>
        </div>
        <div v-show="hasMonthlyTrend" ref="trendChart" class="chart-box" />
        <el-empty v-show="!hasMonthlyTrend && !summaryLoading" :image-size="80" description="暂无月度趋势数据" />
      </div>
    </section>

    <section class="weak-grid">
      <div class="panel weak-panel">
        <div class="panel-head">
          <span>薄弱环节排行</span>
          <span>按绝对差异量</span>
        </div>
        <div v-if="weakLinkGroups.some(group => group.items.length)" class="weak-group-grid">
          <div v-for="group in weakLinkGroups" :key="group.dimension" class="weak-group">
            <div class="weak-group-title">{{ group.title }}</div>
            <div v-if="group.items.length" class="rank-list">
              <div v-for="(item, index) in group.items" :key="`${item.dimension}-${item.targetCode}-${index}`" :class="['rank-item', rankAccent(item)]">
                <span class="rank-no">{{ padRank(index + 1) }}</span>
                <div class="rank-main">
                  <div class="rank-title">{{ item.targetName || item.targetCode || '--' }}</div>
                  <div class="rank-sub">
                    {{ number(item.diffCount) }} 次 / {{ formatWeight(item.absDiffWeight) }}
                  </div>
                  <div class="rank-suggestion">{{ item.suggestion || '建议复核盘点、单据和现场计量记录' }}</div>
                </div>
                <el-tag size="small" :type="diffTagType(item.mainDiffType)">{{ item.mainDiffTypeName || diffTypeName(item.mainDiffType) }}</el-tag>
              </div>
            </div>
            <el-empty v-else :image-size="64" description="暂无数据" />
          </div>
        </div>
        <el-empty v-else :image-size="80" description="暂无薄弱环节" />
      </div>
    </section>

    <section class="table-panel">
      <div class="panel-head table-head">
        <span>盘点差异明细</span>
        <span>{{ tableTotal }} 条</span>
      </div>
      <el-table
        v-loading="pageLoading"
        :data="records"
        border
        stripe
        size="small"
        empty-text="当前筛选条件下暂无盘点差异明细"
        style="width: 100%"
      >
        <el-table-column prop="sheetNo" label="盘点单号" min-width="150" show-overflow-tooltip />
        <el-table-column prop="countTime" label="盘点时间" min-width="150" show-overflow-tooltip />
        <el-table-column label="库区/库位" min-width="170" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ warehouseText(row) }}</template>
        </el-table-column>
        <el-table-column label="客户" min-width="150" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.custName || row.custNo || '--' }}</template>
        </el-table-column>
        <el-table-column label="煤种" min-width="120" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.cargoName || row.cargoNo || '--' }}</template>
        </el-table-column>
        <el-table-column label="账面(吨)" width="116" align="right">
          <template slot-scope="{ row }">{{ formatNumber(row.bookWeight) }}</template>
        </el-table-column>
        <el-table-column label="实盘(吨)" width="116" align="right">
          <template slot-scope="{ row }">{{ formatNumber(row.actualWeight) }}</template>
        </el-table-column>
        <el-table-column label="差异(吨)" width="116" align="right">
          <template slot-scope="{ row }">
            <span :class="diffClass(row.diffWeight)">{{ signedNumber(row.diffWeight) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="差异率" width="92" align="right">
          <template slot-scope="{ row }">{{ formatRate(row.diffRate) }}</template>
        </el-table-column>
        <el-table-column label="分类" width="104" align="center">
          <template slot-scope="{ row }">
            <el-tag size="small" :type="diffTagType(row.diffType)">{{ row.diffTypeName || diffTypeName(row.diffType) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="diffReason" label="差异原因" min-width="180" show-overflow-tooltip />
        <el-table-column label="操作" width="82" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="openDetail(row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        class="page-bar"
        background
        layout="total, sizes, prev, pager, next, jumper"
        :current-page.sync="page.currentPage"
        :page-size.sync="page.pageSize"
        :page-sizes="[10, 20, 50, 100]"
        :total="tableTotal"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </section>

    <el-drawer
      title="盘点差异明细"
      :visible.sync="detailVisible"
      direction="rtl"
      size="620px"
      custom-class="annual-diff-drawer"
    >
      <div v-loading="detailLoading" class="drawer-body">
        <el-descriptions v-if="detailSheet" :column="1" size="small" border class="sheet-desc">
          <el-descriptions-item label="盘点单号">{{ detailSheet.sheetNo || '--' }}</el-descriptions-item>
          <el-descriptions-item label="盘点时间">{{ detailSheet.countTime || '--' }}</el-descriptions-item>
          <el-descriptions-item label="盘点人">{{ detailSheet.counterName || '--' }}</el-descriptions-item>
          <el-descriptions-item label="库区">{{ detailSheet.stockName || detailSheet.stockCode || '--' }}</el-descriptions-item>
          <el-descriptions-item label="状态">{{ detailSheet.sheetStatusName || detailSheet.sheetStatus || '--' }}</el-descriptions-item>
        </el-descriptions>
        <el-empty v-else-if="!detailLoading" :image-size="80" description="暂无盘点单信息" />
        <el-table
          :data="detailLines"
          border
          stripe
          size="small"
          empty-text="暂无盘点差异明细"
          style="width: 100%"
        >
          <el-table-column label="库位" min-width="110" show-overflow-tooltip>
            <template slot-scope="{ row }">{{ row.locationName || row.locationCode || '--' }}</template>
          </el-table-column>
          <el-table-column label="客户" min-width="130" show-overflow-tooltip>
            <template slot-scope="{ row }">{{ row.custName || row.custNo || '--' }}</template>
          </el-table-column>
          <el-table-column label="煤种" min-width="110" show-overflow-tooltip>
            <template slot-scope="{ row }">{{ row.cargoName || row.cargoNo || '--' }}</template>
          </el-table-column>
          <el-table-column label="账面" width="90" align="right">
            <template slot-scope="{ row }">{{ formatNumber(row.bookWeight) }}</template>
          </el-table-column>
          <el-table-column label="实盘" width="90" align="right">
            <template slot-scope="{ row }">{{ formatNumber(row.actualWeight) }}</template>
          </el-table-column>
          <el-table-column label="差异" width="90" align="right">
            <template slot-scope="{ row }">
              <span :class="diffClass(row.diffWeight)">{{ signedNumber(row.diffWeight) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="分类" width="102" align="center">
            <template slot-scope="{ row }">
              <el-tag size="small" :type="diffTagType(row.diffType)">{{ row.diffTypeName || diffTypeName(row.diffType) }}</el-tag>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { mapGetters } from 'vuex'
import { downloadXls } from '@/util/util'
import {
  exportAnnualDiffExcel,
  fetchAnnualDiffCustomerOptions,
  fetchAnnualDiffDashboard,
  fetchAnnualDiffDetailPage,
  fetchAnnualDiffSheetDetail,
  fetchCargoOptions,
  fetchLocationOptions,
  fetchStoreOptions
} from './api'

const EXPORT_PERMISSION_CODE = 'automobile-management:Intelligent-stock-analysis:index:export'

const DIFF_TYPES = [
  { label: '计量误差', value: 'MEASURE_ERROR' },
  { label: '损耗异常', value: 'LOSS_ABNORMAL' },
  { label: '录入错误', value: 'ENTRY_ERROR' },
  { label: '其他', value: 'OTHER' }
]

function currentYear() {
  return String(new Date().getFullYear())
}

function unwrapData(res) {
  const body = res && res.data
  if (!body) return {}
  return body.data !== undefined ? body.data : body
}

function unwrapList(res) {
  const data = unwrapData(res)
  if (Array.isArray(data)) return data
  if (data && Array.isArray(data.records)) return data.records
  return []
}

export default {
  name: 'IntelligentStockAnalysis',
  data() {
    return {
      queryForm: {
        year: currentYear(),
        custNo: '',
        cargoNo: '',
        stockCode: '',
        locationCode: '',
        diffType: ''
      },
      page: {
        currentPage: 1,
        pageSize: 10
      },
      summaryLoading: false,
      pageLoading: false,
      exportLoading: false,
      detailLoading: false,
      customerLoading: false,
      cargoLoading: false,
      storeLoading: false,
      locationLoading: false,
      pageError: '',
      dashboard: {
        summary: {},
        typeDistribution: [],
        monthlyTrend: [],
        weakLinks: []
      },
      records: [],
      tableTotal: 0,
      detailVisible: false,
      detailSheet: null,
      detailLines: [],
      customerOptions: [],
      cargoOptions: [],
      storeOptions: [],
      locationOptions: [],
      typeChart: null,
      trendChart: null,
      diffTypeOptions: DIFF_TYPES
    }
  },
  computed: {
    ...mapGetters(['permission']),
    canExport() {
      const permission = this.permission || {}
      return permission[EXPORT_PERMISSION_CODE] !== false
    },
    summary() {
      return this.dashboard.summary || {}
    },
    weakLinks() {
      return this.dashboard.weakLinks || []
    },
    weakLinkGroups() {
      const rows = this.weakLinks || []
      return [
        { dimension: 'LOCATION', title: '高差异库位', items: rows.filter(item => item.dimension === 'LOCATION').slice(0, 5) },
        { dimension: 'CUSTOMER', title: '高差异客户', items: rows.filter(item => item.dimension === 'CUSTOMER').slice(0, 5) },
        { dimension: 'CARGO', title: '高差异煤种', items: rows.filter(item => item.dimension === 'CARGO').slice(0, 5) }
      ]
    },
    hasTypeDistribution() {
      return (this.dashboard.typeDistribution || []).length > 0
    },
    hasMonthlyTrend() {
      return (this.dashboard.monthlyTrend || []).length > 0
    },
    summaryCards() {
      const sheetCount = this.number(this.summary.sheetCount)
      const lineCount = this.number(this.summary.lineCount)
      const diffLineCount = this.number(this.summary.diffLineCount)
      return [
        {
          key: 'sheet',
          label: '年度盘点单',
          value: sheetCount,
          unit: '单',
          sub: `覆盖 ${lineCount} 条盘点明细`,
          accent: 'primary'
        },
        {
          key: 'line',
          label: '差异明细',
          value: diffLineCount,
          unit: '条',
          sub: `占明细 ${this.formatRate(lineCount ? diffLineCount / lineCount * 100 : 0)}`,
          accent: 'danger'
        },
        {
          key: 'weight',
          label: '全年差异量',
          value: this.signedNumber(this.summary.netDiffWeight),
          unit: '吨',
          sub: `绝对差异 ${this.formatWeight(this.summary.absDiffWeight)}`,
          accent: this.number(this.summary.netDiffWeight) < 0 ? 'danger' : 'warn'
        },
        {
          key: 'rate',
          label: '综合差异率',
          value: this.formatNumber(this.summary.diffRate),
          unit: '%',
          sub: `绝对差异率 ${this.formatRate(this.summary.absDiffRate)}`,
          accent: Math.abs(this.number(this.summary.diffRate)) > 5 ? 'danger' : 'ok'
        }
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initCharts()
      window.addEventListener('resize', this.resizeCharts)
      this.loadCustomerOptions('')
      this.loadCargoOptions('')
      this.loadStoreOptions('')
      this.loadLocationOptions('')
      this.loadData()
    })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeCharts)
    this.disposeCharts()
  },
  methods: {
    buildParams() {
      return {
        year: Number(this.queryForm.year || currentYear()),
        custNo: this.queryForm.custNo || undefined,
        cargoNo: this.queryForm.cargoNo || undefined,
        stockCode: this.queryForm.stockCode || undefined,
        locationCode: this.queryForm.locationCode || undefined,
        diffType: this.queryForm.diffType || undefined
      }
    },
    async loadData() {
      this.summaryLoading = true
      this.pageLoading = true
      this.pageError = ''
      const params = this.buildParams()
      try {
        const [dashboardRes, pageRes] = await Promise.all([
          fetchAnnualDiffDashboard(params),
          fetchAnnualDiffDetailPage({
            ...params,
            page: this.page.currentPage,
            size: this.page.pageSize
          })
        ])
        this.dashboard = {
          summary: {},
          typeDistribution: [],
          monthlyTrend: [],
          weakLinks: [],
          ...unwrapData(dashboardRes)
        }
        const pageData = unwrapData(pageRes)
        this.records = pageData.records || []
        this.tableTotal = Number(pageData.total || 0)
        this.$nextTick(this.renderCharts)
      } catch (e) {
        this.dashboard = { summary: {}, typeDistribution: [], monthlyTrend: [], weakLinks: [] }
        this.records = []
        this.tableTotal = 0
        this.pageError = '全年账实差异数据加载失败，请稍后重试'
        this.$nextTick(this.renderCharts)
      } finally {
        this.summaryLoading = false
        this.pageLoading = false
      }
    },
    handleSearch() {
      this.page.currentPage = 1
      this.loadData()
    },
    handleReset() {
      this.queryForm = {
        year: currentYear(),
        custNo: '',
        cargoNo: '',
        stockCode: '',
        locationCode: '',
        diffType: ''
      }
      this.page.currentPage = 1
      this.loadCustomerOptions('')
      this.loadCargoOptions('')
      this.loadStoreOptions('')
      this.loadLocationOptions('')
      this.loadData()
    },
    handleYearChange() {
      this.queryForm.custNo = ''
      this.customerOptions = []
      this.loadCustomerOptions('')
    },
    handleSizeChange(size) {
      this.page.pageSize = size
      this.page.currentPage = 1
      this.loadData()
    },
    handleCurrentChange(page) {
      this.page.currentPage = page
      this.loadData()
    },
    async handleExport() {
      if (!this.canExport) return
      if (!this.tableTotal) {
        this.$message.warning('当前筛选条件下暂无可导出数据')
        return
      }
      this.exportLoading = true
      try {
        const res = await exportAnnualDiffExcel(this.buildParams())
        downloadXls(res.data, `全年账实差异分析_${this.queryForm.year || currentYear()}.xlsx`)
        this.$message.success('导出成功')
      } catch (e) {
        this.$message.error((e && e.message) || '导出失败')
      } finally {
        this.exportLoading = false
      }
    },
    async openDetail(row) {
      const sheetId = row && row.sheetId
      if (!sheetId) {
        this.$message.warning('缺少盘点单标识，无法查看详情')
        return
      }
      this.detailVisible = true
      this.detailLoading = true
      this.detailSheet = null
      this.detailLines = []
      try {
        const data = unwrapData(await fetchAnnualDiffSheetDetail(sheetId, { year: Number(this.queryForm.year || currentYear()) }))
        this.detailSheet = data.sheet || null
        this.detailLines = data.lines || []
      } catch (e) {
        this.$message.error('盘点差异详情加载失败')
      } finally {
        this.detailLoading = false
      }
    },
    async loadCustomerOptions(keyword) {
      this.customerLoading = true
      try {
        this.customerOptions = unwrapList(await fetchAnnualDiffCustomerOptions({
          deptName: keyword || ''
        }))
      } catch (e) {
        this.customerOptions = []
      } finally {
        this.customerLoading = false
      }
    },
    async loadCargoOptions(keyword) {
      this.cargoLoading = true
      try {
        this.cargoOptions = unwrapList(await fetchCargoOptions({ varName: keyword || '' }))
      } catch (e) {
        this.cargoOptions = []
      } finally {
        this.cargoLoading = false
      }
    },
    async loadStoreOptions(keyword) {
      this.storeLoading = true
      try {
        this.storeOptions = unwrapList(await fetchStoreOptions({ storeName: keyword || '' }))
      } catch (e) {
        this.storeOptions = []
      } finally {
        this.storeLoading = false
      }
    },
    async loadLocationOptions(keyword) {
      this.locationLoading = true
      try {
        this.locationOptions = unwrapList(await fetchLocationOptions({ locationName: keyword || '' }))
      } catch (e) {
        this.locationOptions = []
      } finally {
        this.locationLoading = false
      }
    },
    initCharts() {
      if (this.$refs.typeChart && !this.typeChart) this.typeChart = echarts.init(this.$refs.typeChart)
      if (this.$refs.trendChart && !this.trendChart) this.trendChart = echarts.init(this.$refs.trendChart)
    },
    renderCharts() {
      this.initCharts()
      this.renderTypeChart()
      this.renderTrendChart()
      this.resizeCharts()
    },
    renderTypeChart() {
      if (!this.typeChart) return
      const rows = this.dashboard.typeDistribution || []
      this.typeChart.setOption({
        color: ['#409eff', '#e6a23c', '#f56c6c', '#909399'],
        tooltip: {
          trigger: 'item',
          formatter: item => `${item.name}<br/>明细：${item.data.count || 0} 条<br/>绝对差异：${this.formatWeight(item.data.absDiffWeight)}<br/>占比：${this.formatRate(item.data.percent)}`
        },
        legend: { top: 0, left: 'center' },
        series: [
          {
            type: 'pie',
            radius: ['42%', '68%'],
            center: ['50%', '56%'],
            label: { formatter: '{b}\n{d}%' },
            data: rows.map(item => ({
              name: item.diffTypeName || this.diffTypeName(item.diffType),
              value: item.count || 0,
              count: item.count || 0,
              absDiffWeight: item.absDiffWeight,
              percent: item.percent
            }))
          }
        ]
      }, true)
    },
    renderTrendChart() {
      if (!this.trendChart) return
      const rows = this.dashboard.monthlyTrend || []
      this.trendChart.setOption({
        color: ['#409eff', '#f56c6c', '#67c23a'],
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' }
        },
        legend: { top: 0 },
        grid: { left: 46, right: 46, top: 42, bottom: 30 },
        xAxis: {
          type: 'category',
          axisTick: { show: false },
          data: rows.map(item => item.monthLabel || `${item.month}月`)
        },
        yAxis: [
          { type: 'value', name: '吨' },
          { type: 'value', name: '%', axisLabel: { formatter: '{value}%' } }
        ],
        series: [
          { name: '账面', type: 'bar', barMaxWidth: 22, data: rows.map(item => this.number(item.totalBookWeight)) },
          { name: '实盘', type: 'bar', barMaxWidth: 22, data: rows.map(item => this.number(item.totalActualWeight)) },
          { name: '差异率', type: 'line', yAxisIndex: 1, smooth: true, data: rows.map(item => this.number(item.diffRate)) }
        ]
      }, true)
    },
    resizeCharts() {
      this.typeChart && this.typeChart.resize()
      this.trendChart && this.trendChart.resize()
    },
    disposeCharts() {
      this.typeChart && this.typeChart.dispose()
      this.trendChart && this.trendChart.dispose()
      this.typeChart = null
      this.trendChart = null
    },
    warehouseText(row) {
      const stock = row.stockName || row.stockCode || '--'
      const location = row.locationName || row.locationCode || '--'
      return `${stock} / ${location}`
    },
    dimensionName(value) {
      const map = {
        LOCATION: '库位',
        CUSTOMER: '客户',
        CARGO: '煤种',
        STOCK: '库区'
      }
      return map[value] || value || '--'
    },
    diffTypeName(value) {
      const hit = DIFF_TYPES.find(item => item.value === value)
      return hit ? hit.label : '其他'
    },
    diffTagType(value) {
      if (value === 'LOSS_ABNORMAL') return 'danger'
      if (value === 'ENTRY_ERROR') return 'warning'
      if (value === 'MEASURE_ERROR') return ''
      return 'info'
    },
    rankAccent(item) {
      if (item.mainDiffType === 'LOSS_ABNORMAL') return 'danger'
      if (item.mainDiffType === 'ENTRY_ERROR') return 'warn'
      return 'primary'
    },
    diffClass(value) {
      const n = this.number(value)
      if (n < 0) return 'num-loss'
      if (n > 0) return 'num-profit'
      return ''
    },
    padRank(index) {
      return String(index).padStart(2, '0')
    },
    signedNumber(value) {
      const n = this.number(value)
      const text = this.formatNumber(Math.abs(n))
      if (n > 0) return `+${text}`
      if (n < 0) return `-${text}`
      return text
    },
    formatWeight(value) {
      return `${this.formatNumber(value)} 吨`
    },
    formatRate(value) {
      if (value === null || value === undefined || value === '') return '--'
      return `${this.formatNumber(value)}%`
    },
    formatNumber(value) {
      const n = this.number(value)
      return n.toLocaleString('zh-CN', { maximumFractionDigits: 2 })
    },
    number(value) {
      return Number(value || 0)
    }
  }
}
</script>

<style scoped lang="scss">
.annual-diff-page {
  padding: 12px;
  background: #f3f5f8;
  min-height: 100%;
}

.filter-panel,
.panel,
.table-panel,
.metric-card {
  background: #fff;
  border: 1px solid #e5e9f0;
  border-radius: 4px;
}

.filter-panel {
  padding: 12px 12px 2px;
  margin-bottom: 12px;
}

.filter-form {
  display: flex;
  flex-wrap: wrap;

  ::v-deep .el-form-item {
    margin-right: 12px;
    margin-bottom: 10px;
  }

  ::v-deep .el-date-editor,
  ::v-deep .el-select {
    width: 170px;
  }
}

.filter-actions {
  ::v-deep .el-form-item__content {
    margin-left: 0 !important;
    white-space: nowrap;
  }
}

.state-alert {
  margin-bottom: 12px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 12px;
}

.metric-card {
  min-height: 96px;
  padding: 14px;
  border-top: 3px solid #409eff;
}

.metric-card.warn {
  border-top-color: #e6a23c;
}

.metric-card.danger {
  border-top-color: #f56c6c;
}

.metric-card.ok {
  border-top-color: #67c23a;
}

.metric-head {
  display: flex;
  justify-content: space-between;
  color: #606266;

  span:last-child {
    color: #909399;
    font-size: 12px;
  }
}

.metric-value {
  margin-top: 10px;
  font-size: 25px;
  line-height: 32px;
  font-weight: 600;
  color: #303133;

  small {
    margin-left: 4px;
    color: #909399;
    font-size: 12px;
    font-weight: 400;
  }
}

.metric-foot {
  margin-top: 8px;
  color: #909399;
  font-size: 12px;
}

.analysis-grid {
  display: grid;
  grid-template-columns: 1fr 1.1fr;
  gap: 12px;
  margin-bottom: 12px;
}

.weak-grid {
  margin-bottom: 12px;
}

.panel {
  padding: 12px;
  min-height: 280px;
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  font-weight: 600;
  color: #303133;

  span:last-child {
    color: #909399;
    font-size: 12px;
    font-weight: 400;
  }
}

.chart-box {
  width: 100%;
  height: 230px;
}

.weak-panel {
  min-height: 220px;
}

.weak-group-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(240px, 1fr));
  gap: 12px;
}

.weak-group {
  min-width: 0;
}

.weak-group-title {
  margin-bottom: 8px;
  color: #606266;
  font-weight: 600;
}

.rank-list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}

.rank-item {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) auto;
  gap: 8px;
  align-items: start;
  padding: 10px;
  background: #f8fbff;
  border-left: 3px solid #409eff;
}

.rank-item.warn {
  border-left-color: #e6a23c;
  background: #fffaf0;
}

.rank-item.danger {
  border-left-color: #f56c6c;
  background: #fff7f7;
}

.rank-no {
  color: #909399;
  font-weight: 600;
}

.rank-main {
  min-width: 0;
}

.rank-title {
  font-weight: 600;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rank-sub,
.rank-suggestion {
  margin-top: 4px;
  color: #909399;
  font-size: 12px;
  line-height: 18px;
}

.table-panel {
  padding: 12px;
}

.table-head {
  margin-bottom: 8px;
}

.page-bar {
  margin-top: 12px;
  text-align: right;
}

.num-loss {
  color: #f56c6c;
  font-weight: 600;
}

.num-profit {
  color: #67c23a;
  font-weight: 600;
}

.drawer-body {
  padding: 0 16px 16px;
}

.sheet-desc {
  margin-bottom: 12px;
}

@media (max-width: 1200px) {
  .summary-grid {
    grid-template-columns: repeat(2, minmax(180px, 1fr));
  }

  .analysis-grid {
    grid-template-columns: 1fr;
  }

  .rank-list {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 720px) {
  .annual-diff-page {
    padding: 8px;
  }

  .summary-grid {
    grid-template-columns: 1fr;
  }

  .filter-form {
    display: block;

    ::v-deep .el-form-item {
      margin-right: 0;
    }

    ::v-deep .el-date-editor,
    ::v-deep .el-select {
      width: 100%;
    }
  }
}
</style>
